import React, { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import apiServices from '../../services/service';
import Loader from '../../shared/component/Loader';
import { getTagsByModule } from '../../redux/selector';
import { fetchDataAndUpdateStore } from '../../redux/actions';
import { Ic } from "../../shared/component/GosIcon";

/* ------------------------------------------------------------------ *
 * GrowthOS — Manage Tags (design growthos/TagsManager.jsx)
 * Shared across Lead / Contact / Account / Opportunity / Campaign
 * (module + moduleApi arrive via location.state, set by each list page).
 *
 * Real tag records only carry { id, name[], createdBy } — the design's
 * color / description / usage-count / last-used are mock-only, so those
 * columns + the color swatches + description field are dropped and the
 * tag / avatar colors are derived deterministically instead. Create /
 * edit / delete are wired to the real createTags / updateTags /
 * deleteTagsByModule APIs (same calls the legacy page used).
 * ------------------------------------------------------------------ */

const PALETTE = [
  '#c96342', '#2e63a6', '#2c7a45', '#9a751c', '#6a4fae', '#b34480',
  '#0e8c6f', '#c0473f', '#4f6b8f', '#7c4ddb', '#0891b2', '#6b6353',
];
const hashIdx = (s, n) => {
  let h = 0;
  for (let i = 0; i < (s || '').length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h % n;
};
const tagColor = (name) => PALETTE[hashIdx(name, PALETTE.length)];
const tint = (hex, a) => {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
};
const initials = (n) =>
  (n || '')
    .trim()
    .split(/\s+/)
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase() || '?';
const tagLabel = (name) => (Array.isArray(name) ? name.join(', ') : name || '');

const MODULE_LABEL = {
  Lead: 'Leads',
  Contact: 'Contacts',
  Account: 'Accounts',
  Opportunity: 'Opportunities',
  Campaign: 'Campaigns',
};

/* ---- inline icon set (24-box stroke, GrowthOS strokeWidth 1.7) ---- */

/* ---- click-outside ---- */
const useOutside = (open, onClose) => {
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const fn = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, [open, onClose]);
  return ref;
};

const TagChip = ({ name, size }) => {
  const color = tagColor(name);
  return (
    <span
      className="gos-tagm-chip"
      style={{
        color,
        background: tint(color, 0.12),
        height: size === 'lg' ? 30 : 24,
        fontSize: size === 'lg' ? 13.5 : 12.5,
        padding: size === 'lg' ? '0 12px' : '0 10px',
      }}
    >
      <span className="gos-tagm-dot" style={{ background: color }} />
      {name || 'Tag name'}
    </span>
  );
};

/* ---- create / edit modal (name only — color/desc have no backend) ---- */
const TagModal = ({ open, initial, saving, onClose, onSave }) => {
  const [name, setName] = useState('');
  useEffect(() => {
    if (open) setName(initial ? tagLabel(initial.name) : '');
  }, [open, initial]);
  if (!open) return null;
  const editing = !!initial;
  const trimmed = name.trim();
  const color = tagColor(trimmed);
  return (
    <div
      className="gos-import-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="gos-import-modal gos-tagm-modal" style={{ maxWidth: 460 }}>
        <div className="gos-import-head">
          <div className="gos-import-head-l">
            <span
              className="gos-import-step-ic"
              style={{ background: tint(color, 0.14), color }}
            >
              <Ic name="tag" size={17} />
            </span>
            <div>
              <div className="gos-import-title">{editing ? 'Edit tag' : 'New tag'}</div>
              <div className="gos-import-sub">
                {editing ? 'Update the label.' : 'Create a label to segment records.'}
              </div>
            </div>
          </div>
          <button className="gos-import-close" onClick={onClose} aria-label="Close">
            <Ic name="x" size={18} />
          </button>
        </div>

        <div className="gos-tagm-modal-body">
          <div className="gos-tagm-preview">
            <span className="gos-tagm-preview-lbl">Preview</span>
            <TagChip name={trimmed} size="lg" />
          </div>
          <label className="gos-tagm-flabel">
            Tag name<span className="gos-tagm-req"> *</span>
          </label>
          <input
            className="gos-tagm-input"
            placeholder="e.g. Hot"
            value={name}
            maxLength={40}
            autoFocus
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && trimmed && !saving) onSave(trimmed);
            }}
          />
        </div>

        <div className="gos-import-foot end">
          <div className="gos-foot-actions">
            <button className="gos-btn gos-btn-ghost" onClick={onClose}>
              Cancel
            </button>
            <button
              className="gos-btn gos-btn-primary"
              disabled={!trimmed || saving}
              onClick={() => onSave(trimmed)}
            >
              {saving ? (
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
              ) : (
                <Ic name="check" size={16} />
              )}
              {editing ? 'Save changes' : 'Create tag'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ConfirmDelete = ({ tag, saving, moduleLabel, onClose, onConfirm }) => {
  if (!tag) return null;
  return (
    <div
      className="gos-import-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="gos-import-modal gos-tagm-modal" style={{ maxWidth: 420 }}>
        <div className="gos-import-head">
          <div className="gos-import-title">Delete tag</div>
          <button className="gos-import-close" onClick={onClose} aria-label="Close">
            <Ic name="x" size={18} />
          </button>
        </div>
        <div className="gos-tagm-del-body">
          <p>
            Delete <TagChip name={tagLabel(tag.name)} /> ? It will be removed from every{' '}
            {moduleLabel.replace(/s$/, '').toLowerCase()} record it&apos;s applied to. This can&apos;t
            be undone.
          </p>
        </div>
        <div className="gos-import-foot end">
          <div className="gos-foot-actions">
            <button className="gos-btn gos-btn-ghost" onClick={onClose}>
              Cancel
            </button>
            <button className="gos-btn gos-btn-danger" disabled={saving} onClick={onConfirm}>
              {saving ? (
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
              ) : (
                <Ic name="trash" size={15} />
              )}
              Delete tag
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const RowMenu = ({ onEdit, onDelete }) => {
  const [open, setOpen] = useState(false);
  const ref = useOutside(open, () => setOpen(false));
  return (
    <div className="gos-tagm-menu" ref={ref}>
      <button
        className={`gos-tagm-iconbtn${open ? ' on' : ''}`}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        aria-label="Row actions"
      >
        <Ic name="dots" size={16} />
      </button>
      {open && (
        <div className="gos-tagm-dd">
          <button
            className="gos-tagm-dd-item"
            onClick={(e) => {
              e.stopPropagation();
              setOpen(false);
              onEdit();
            }}
          >
            <Ic name="edit" size={14} /> Edit
          </button>
          <button
            className="gos-tagm-dd-item gos-danger"
            onClick={(e) => {
              e.stopPropagation();
              setOpen(false);
              onDelete();
            }}
          >
            <Ic name="trash" size={14} /> Delete
          </button>
        </div>
      )}
    </div>
  );
};

const SORT_LABELS = { az: 'Name (A–Z)', za: 'Name (Z–A)' };

const ManageTagPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const module = location?.state?.module || ''; // store key (e.g. 'leads')
  const moduleApi = location?.state?.moduleApi || ''; // singular (e.g. 'Lead')
  const moduleLabel = MODULE_LABEL[moduleApi] || 'Records';

  const tagsByModule = useSelector(getTagsByModule(module));

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState('');
  const [sort, setSort] = useState('az');
  const [sortOpen, setSortOpen] = useState(false);
  const sortRef = useOutside(sortOpen, () => setSortOpen(false));

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [del, setDel] = useState(null);

  const refetch = useCallback(() => {
    if (!moduleApi) return;
    dispatch(
      fetchDataAndUpdateStore({ key: 'tagsByModule', inputData: moduleApi, storeKey: module })
    );
  }, [dispatch, moduleApi, module]);

  // Populate the store on direct navigation / refresh (legacy relied on the list page).
  useEffect(() => {
    if (moduleApi && (!tagsByModule || tagsByModule.length === 0)) refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleApi]);

  const rows = useMemo(() => {
    let list = Array.isArray(tagsByModule) ? [...tagsByModule] : [];
    const s = q.trim().toLowerCase();
    if (s) {
      list = list.filter(
        (t) =>
          tagLabel(t.name).toLowerCase().includes(s) ||
          (t.createdBy || '').toLowerCase().includes(s)
      );
    }
    list.sort((a, b) => {
      const cmp = tagLabel(a.name).localeCompare(tagLabel(b.name));
      return sort === 'za' ? -cmp : cmp;
    });
    return list;
  }, [tagsByModule, q, sort]);

  const contributors = useMemo(
    () => new Set((tagsByModule || []).map((t) => t.createdBy).filter(Boolean)).size,
    [tagsByModule]
  );

  const openCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };
  const openEdit = (tag) => {
    setEditing(tag);
    setModalOpen(true);
  };
  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
  };

  const handleSave = async (name) => {
    setSaving(true);
    try {
      const res = editing
        ? await apiServices.updateTags({ module: moduleApi, id: editing.id, tagName: [name] })
        : await apiServices.createTags({ tagName: [name], module: moduleApi, moduleId: [''] });
      if (res.status === 200) {
        toast.success(editing ? 'Tag updated successfully!' : 'Tag created successfully!', {
          position: 'top-right',
        });
        closeModal();
        refetch();
      } else {
        toast.error('Failed to save tag.', { position: 'top-right' });
      }
    } catch (err) {
      console.error('Error saving tag:', err);
      toast.error('Failed to save tag.', { position: 'top-right' });
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!del) return;
    setSaving(true);
    try {
      const res = await apiServices.deleteTagsByModule({ id: del.id, module: moduleApi });
      if (res.status === 200) {
        toast.success('Tag deleted successfully!', { position: 'top-right' });
        setDel(null);
        refetch();
      } else {
        toast.error('Error deleting tag.', { position: 'top-right' });
      }
    } catch (err) {
      console.error('Error deleting tag:', err);
      toast.error('Error deleting tag.', { position: 'top-right' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {loading && <Loader />}
      <div className="inner-page">
        <div className="gos-tagm">
          {/* head */}
          <div className="gos-tagm-head">
            <div>
              <button className="gos-tagm-back" onClick={() => navigate(-1)}>
                <Ic name="back" size={15} /> {moduleLabel}
              </button>
              <div className="gos-tagm-h1">Manage Tags</div>
              <div className="gos-tagm-sub">
                Labels used to segment and organize your {moduleLabel.toLowerCase()}
              </div>
            </div>
            <button className="gos-btn gos-btn-primary" onClick={openCreate}>
              <Ic name="plus" size={16} /> New Tag
            </button>
          </div>

          {/* stats (only real / derivable metrics) */}
          <div className="gos-tagm-stats">
            <div className="gos-tagm-stat">
              <span className="gos-tagm-stat-num">{(tagsByModule || []).length}</span>
              <span className="gos-tagm-stat-lbl">Total tags</span>
            </div>
            <div className="gos-tagm-stat">
              <span className="gos-tagm-stat-num">{contributors}</span>
              <span className="gos-tagm-stat-lbl">Contributors</span>
            </div>
          </div>

          {/* card: toolbar + table */}
          <div className="gos-tagm-card">
            <div className="gos-tagm-toolbar">
              <div className="gos-tpl-search" style={{ width: 300 }}>
                <Ic name="search" size={16} />
                <input
                  placeholder="Search tags…"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                />
                {q && (
                  <button className="gos-tpl-search-clear" onClick={() => setQ('')} aria-label="Clear">
                    <Ic name="x" size={14} />
                  </button>
                )}
              </div>
              <span className="gos-tagm-count-lbl">
                <strong>{rows.length}</strong>
                {rows.length === 1 ? ' tag' : ' tags'}
              </span>
              <div className="gos-tagm-grow" />
              <div className="gos-tagm-menu" ref={sortRef}>
                <button
                  className={`gos-btn gos-btn-ghost${sortOpen ? ' on' : ''}`}
                  onClick={() => setSortOpen((o) => !o)}
                >
                  {SORT_LABELS[sort]}
                  <Ic name="chevD" size={14} />
                </button>
                {sortOpen && (
                  <div className="gos-tagm-dd" style={{ minWidth: 180 }}>
                    {Object.keys(SORT_LABELS).map((k) => (
                      <button
                        key={k}
                        className="gos-tagm-dd-item"
                        onClick={() => {
                          setSort(k);
                          setSortOpen(false);
                        }}
                      >
                        <span className={`gos-tpl-check${k === sort ? ' on' : ''}`}>
                          {k === sort && <Ic name="check" size={11} />}
                        </span>
                        {SORT_LABELS[k]}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="gos-tagm-tbl-scroll">
              <table className="gos-tagm-tbl">
                <thead>
                  <tr>
                    <th>Tag</th>
                    <th>Created by</th>
                    <th style={{ width: 52 }} />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((t) => {
                    const name = tagLabel(t.name);
                    return (
                      <tr key={t.id} onClick={() => openEdit(t)}>
                        <td>
                          <TagChip name={name} />
                        </td>
                        <td>
                          <div className="gos-tagm-byline">
                            <span
                              className="gos-tagm-ava"
                              style={{ background: tagColor(t.createdBy || '?') }}
                            >
                              {initials(t.createdBy)}
                            </span>
                            <span className="gos-tagm-by">{t.createdBy || '—'}</span>
                          </div>
                        </td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <RowMenu onEdit={() => openEdit(t)} onDelete={() => setDel(t)} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {rows.length === 0 && (
                <div className="gos-tagm-empty">
                  {q ? `No tags match “${q}”.` : 'No tags yet. Create your first tag.'}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <TagModal
        open={modalOpen}
        initial={editing}
        saving={saving}
        onClose={closeModal}
        onSave={handleSave}
      />
      <ConfirmDelete
        tag={del}
        saving={saving}
        moduleLabel={moduleLabel}
        onClose={() => setDel(null)}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
};

export default ManageTagPage;
